<template>
  <div
    class="cn-header"
    :class="{
      'cn-header--top-block-hide': !isTopBlockShown,
    }"
  >
    <div class="cn-header__top">
      <div>
        <div class="container">
          <div class="cn-header__top-block">
            <div class="cn-header__to-main">
              <UiLink
                v-for="link in menuTopbar"
                :key="link.id || link.url"
                :to="link.url"
              >
                {{ link.name }}
              </UiLink>
            </div>
            <button
              class="cn-header__search"
              aria-label="search"
              @click="modalSearch.open()"
            >
              <UiIcon name="search" />
            </button>
            <UiModal ref="modalSearch" width="980px">
              <div class="cn-header__search-modal">
                <AppSearch @search="modalSearch.close()" />
              </div>
            </UiModal>
            <div class="cn-header__locale-controls">
              <LocationSwitcher ref="locationSwitcher" />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="headerBottom" class="cn-header__bottom">
      <div class="container">
        <div class="cn-header__bottom-block">
          <div
            class="cn-header__logo"
            :class="{
              'cn-header__logo--bigger': isScrollTop,
            }"
          >
            <UiLink to="/" aria-label="Canyon main page">
              <AppLogo />
            </UiLink>
          </div>
          <AppHeaderMenu :header-ref="headerBottom">
            <template #default="{ activeItem, setActiveItem }">
              <AppHeaderMenuItem
                v-for="(menuItem, i) in menu"
                :key="menuItem.id"
                :menu-item="menuItem"
                :is-active="activeItem === i"
                @set-active-item="setActiveItem(i)"
              />
            </template>
          </AppHeaderMenu>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, computed } from 'vue';
import { useScroll } from '@vueuse/core';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import AppSearch from '@/components/AppSearch';
import AppLogo from '@/components/AppLogo';
import AppHeaderMenuItem from '@/components/AppHeaderMenuItem';
import AppHeaderMenu from '@/components/AppHeaderMenu';
import UiModal from '@/components/ui/UiModal';
import UiLink from '@/components/ui/UiLink';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  menuTopbar: any[];
  menu: any[];
}

defineProps<Props>();

const headerBottom = ref();
const locationSwitcher = ref();
const isTopBlockShown = ref(true);
const modalSearch = ref(null);
let scrollElement = null;
if (import.meta.client) {
  scrollElement = window;
}
const { y, isScrolling, directions } = useScroll(scrollElement);
watch(
  () => directions.bottom,
  (newValue) => {
    if (newValue === true) isTopBlockShown.value = false;
  },
);
watch(
  () => directions.top,
  (newValue) => {
    if (newValue === true) isTopBlockShown.value = true;
  },
);

watch(isScrolling, (newValue) => {
  if (newValue === true) {
    // The watcher is live as soon as setup runs, but the template ref is only
    // filled once the header's children mount. Scrolling before hydration
    // finishes fired this on an empty ref, and the TypeError bubbled up to
    // Nuxt as a full-page 500.
    locationSwitcher.value?.close();
  }
});

const isScrollTop = computed(() => y.value === 0);
</script>

<style lang="scss">
.cn-header {
  --ab-canyon-header-top-color-bg: var(--ab-canyon-color-neutral-light);
  --ab-canyon-header-top-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-header-bottom-color-bg: var(--ab-canyon-color-light);

  transition-duration: 300ms;

  transition-property:
    color,
    background-color,
    border-color,
    text-decoration-color,
    fill,
    stroke,
    opacity,
    box-shadow,
    transform,
    filter,
    -webkit-backdrop-filter;
  transition-property:
    color, background-color, border-color, text-decoration-color, fill, stroke,
    opacity, box-shadow, transform, filter, backdrop-filter;
  transition-property:
    color,
    background-color,
    border-color,
    text-decoration-color,
    fill,
    stroke,
    opacity,
    box-shadow,
    transform,
    filter,
    backdrop-filter,
    -webkit-backdrop-filter;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 0 #00000000,
    0 0 #00000000,
    0 4px 6px -1px #0000001a,
    0 2px 4px -2px #0000001a;
  z-index: 30;
  top: 0px;
  right: 0px;
  left: 0px;
  position: fixed;
  &__top {
    background-color: var(--ab-canyon-header-top-color-bg);
    color: var(--ab-canyon-header-top-color-text);
  }
  &__top-block {
    padding-top: calc(var(--size-base) / 4);
    padding-bottom: calc(var(--size-base) / 4);
    justify-content: space-between;
    align-items: center;
    display: flex;
  }
  &__to-main {
    flex-grow: 1;
    a {
      text-transform: uppercase;
      font-size: 0.875rem;
      line-height: 1.25rem;
      margin-right: var(--size-base);
    }
  }
  &__search {
    padding-left: calc(var(--size-base) / 2);
    padding-right: calc(var(--size-base) / 2);
    .cn-icon {
      width: var(--size-lg);
      height: var(--size-lg);
    }
  }
  &__search-modal {
    padding-right: calc(8 * var(--size-base));
  }
  &__locale-controls {
    display: flex;
    gap: var(--size-base);
    margin-left: var(--size-base);
  }
  &__bottom {
    backdrop-filter: blur(4px);
    position: relative;
    background-color: color-mix(
      in srgb,
      var(--ab-canyon-header-bottom-color-bg),
      transparent 10%
    );
  }
  &__bottom-block {
    justify-content: space-between;
    align-items: stretch;
    display: flex;
  }
  &__logo {
    transition-duration: 500ms;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    padding-top: var(--size-base);
    padding-bottom: var(--size-base);
    width: calc(7 * var(--size-base));
    min-height: 63px;
    &--bigger {
      width: calc(12 * var(--size-base));
      min-height: 85px;
    }
  }
  &--top-block-hide {
    transform: translateY(calc(var(--size-3xl) * -1));
  }
}
</style>
